import { useEffect, useRef } from 'react'
import { useLoomGraph } from '@/hooks/useLoomGraph'
import { renderLoom } from '@/lib/loom-render'
import './Loom.css'

/**
 * The Loom — the Council's agent knowledge graph.
 *
 * A comprehension/exploration view of how the runtime's agents, skills, memory
 * namespaces and learnings weave together. Gated behind the same super-admin
 * guard as the rest of the Command Centre (see ProtectedRoute → SuperAdminGate
 * in App.tsx). Data comes from the read-only `/api/v1/loom/graph` endpoint —
 * runtime-internal only, no tenant/product data. The four pink learning nodes
 * are badged MOCK until the live loop-reflection feed is wired.
 *
 * The graph itself is rendered imperatively with d3 in `renderLoom` — React
 * only owns the container skeleton below (which stays empty in JSX so d3 can
 * populate it without React reconciling those subtrees).
 */
export function Loom() {
  const { data, isLoading, isError, error, refetch } = useLoomGraph()
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!data || !rootRef.current) return
    const cleanup = renderLoom(rootRef.current, data)
    return cleanup
  }, [data])

  return (
    <>
      {isLoading && (
        <div className="loom-root">
          <div className="loom-state">
            <span className="loom-spinner" aria-hidden="true" />
            <span>Weaving the loom…</span>
          </div>
        </div>
      )}

      {isError && (
        <div className="loom-root">
          <div className="loom-state">
            <span>Couldn’t load the agent graph.</span>
            <span className="prov">{error instanceof Error ? error.message : 'Unknown error'}</span>
            <button className="loom-mini" type="button" onClick={() => refetch()}>
              Retry
            </button>
          </div>
        </div>
      )}

      {data && (
        <div className="loom-root" ref={rootRef}>
          <aside className="loom-rail">
            <h1>The&nbsp;Loom</h1>
            <div className="loom-tagline">
              Weaving the Council&rsquo;s threads — every agent, skill and learning on one loom.
            </div>
            <div className="loom-sub" data-el="railmeta" />

            <div className="loom-group">
              <h2>Node type</h2>
              <div data-el="typeFilters" />
            </div>

            <div className="loom-group">
              <h2>Threads</h2>
              <div data-el="linkFilters" />
            </div>

            <div className="loom-group">
              <h2>Focus agent</h2>
              <select data-el="agentSel" defaultValue="">
                <option value="">All agents</option>
              </select>
            </div>

            <div className="loom-group">
              <h2>Search</h2>
              <input type="search" data-el="search" placeholder="agent, tool, namespace…" />
            </div>

            <div className="loom-group">
              <h2>Watch the weave grow</h2>
              <label className="loom-chk">
                <input type="checkbox" data-el="timeOn" /> Enable timeline
              </label>
              <div className="loom-sub" style={{ margin: '6px 0 0' }}>
                Replays the loom thread by thread — when each agent and tool first landed, from this repo&rsquo;s git
                history.
              </div>
            </div>

            <div className="loom-group">
              <h2>Provenance</h2>
              <div className="prov" data-el="prov" />
            </div>
          </aside>

          <main className="loom-canvas" data-el="canvas">
            <div className="loom-legend" data-el="legend" />
            <button
              className="loom-mini"
              data-el="recenter"
              type="button"
              style={{ position: 'absolute', right: 16, top: 14 }}
            >
              Re-center
            </button>
            <svg data-el="svg" />
            <div className="loom-timebar off" data-el="timebar">
              <button className="loom-mini" data-el="play" type="button">
                ▶ Play
              </button>
              <span className="lbl">as of</span>
              <span className="date" data-el="tdate" />
              <input type="range" data-el="tslider" min={0} max={100} defaultValue={100} />
              <span className="stat" data-el="tstat" />
            </div>
          </main>

          <aside className="loom-panel empty" data-el="panel">
            Select a node to inspect it.
          </aside>
        </div>
      )}
    </>
  )
}
